import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, set, push } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import InchargeLayout from './InchargeLayout';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';

const GenerateCertificate = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        qualification: '',
        userId: '',
        userName: '',
        address: '',
        amount: '',
        regClass: '',
        certTerm: '',
        eligibility: '',
        regNo: ''
    });

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        // Fetch approved engineers for certificate generation
        const usersRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubData = onValue(usersRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data)
                    .map(key => ({ id: key, ...data[key] }))
                    .filter(u => u.assistantStatus === 'Approved' || u.assistantStatus === 'Accepted');
                setUsers(list);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const handleUserChange = (e) => {
        const selectedId = e.target.value;
        const user = users.find(u => u.id === selectedId);

        if (user) {
            let qual = user.qualification || user.technicalQualification || '';
            const qualLower = qual.toLowerCase();
            if (qualLower.includes('degree') || qualLower.includes('be') || qualLower.includes('b.e') || qualLower.includes('btech')) {
                qual = 'Degree';
            } else if (qualLower.includes('diploma')) {
                qual = 'Diploma';
            }

            setFormData({
                ...formData,
                userId: selectedId,
                userName: `${user.firstName || ''} ${user.surname || ''}`,
                address: user.address || user.currentAddress || '',
                amount: user.amount || '५०००', // Default or fetched
                regClass: user.category || user.regClass || '',
                eligibility: user.eligibility || '',
                qualification: qual,
                regNo: user.registrationNo || '',
            });
        } else {
            setFormData({
                ...formData,
                userId: '',
                userName: '',
                address: '',
                amount: '',
                regClass: '',
                eligibility: '',
                qualification: '',
                regNo: '',
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.userId || !formData.qualification || !formData.regNo || !formData.certTerm) {
            toast.error("कृपया सर्व आवश्यक माहिती भरा.");
            return;
        }

        try {
            const certRef = ref(db, `ekamwatapzp/Certificates`);
            const newCertRef = push(certRef);
            await set(newCertRef, {
                ...formData,
                generatedAt: Date.now(),
                status: 'Generated'
            });

            // Update user record to mark certificate as generated
            await set(ref(db, `ekamwatapzp/EngineerRegistrations/${formData.userId}/certificateStatus`), 'Generated');

            toast.success("सर्टिफिकेट यशस्वीरित्या जनरेट झाले!");
            navigate(-1);
        } catch (error) {
            console.error("Error generating certificate:", error);
            toast.error("काहीतरी चूक झाली. पुन्हा प्रयत्न करा.");
        }
    };

    const Label = ({ children, required }) => (
        <label className="text-sm font-bold text-gray-600 mb-1.5 flex items-center gap-1">
            {children} {required && <span className="text-red-500">*</span>}
        </label>
    );

    return (
        <InchargeLayout>
            <div className="min-h-screen bg-gray-100/50 flex flex-col ">
                {/* Header Section */}
                <div className="px-6 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <h1 className="text-xl font-bold text-gray-800 tracking-tight">जनरेट सर्टिफिकेट</h1>
                    <div className="flex items-center text-sm font-medium text-gray-500 gap-2">
                        <span>सांगली जिल्हा परिषद</span>
                        <span>/</span>
                        <span className="text-gray-600 font-semibold">जनरेट सर्टिफिकेट</span>
                    </div>
                </div>

                <div className="px-6 pb-12">
                    <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-8 md:p-10">
                        <form onSubmit={handleSubmit} className="space-y-8">

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">

                                {/* Qualification */}
                                <div className="flex flex-col">
                                    <Label required>Qualification (पात्रता)</Label>
                                    <select
                                        value={formData.qualification}
                                        onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                                        className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                                    >
                                        <option value="">Select Qualification</option>
                                        <option value="Degree">Degree</option>
                                        <option value="Diploma">Diploma</option>
                                    </select>
                                </div>

                                {/* Select User */}
                                <div className="flex flex-col">
                                    <Label required>Select User (वापरकर्ता निवडा)</Label>
                                    <select
                                        value={formData.userId}
                                        onChange={handleUserChange}
                                        className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                                    >
                                        <option value="">Select User</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>{u.firstName} {u.surname} ({u.whatsappNo})</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Address */}
                                <div className="flex flex-col">
                                    <Label required>Address (पुर्ण पत्ता )</Label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={formData.address}
                                        className="w-full bg-[#f1f5f9] border border-gray-200 rounded-md px-4 py-2 text-sm text-gray-600 cursor-not-allowed"
                                    />
                                </div>

                                {/* Amount */}
                                <div className="flex flex-col">
                                    <Label required>Amount (रक्कम )</Label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={formData.amount}
                                        className="w-max bg-[#f1f5f9] border border-gray-200 rounded-md px-6 py-2 text-sm text-gray-600 cursor-not-allowed"
                                    />
                                </div>

                                {/* Registration Class */}
                                <div className="flex flex-col">
                                    <Label required>Registration class (नोंदणी वर्ग)</Label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={formData.regClass}
                                        className="w-full bg-[#f1f5f9] border border-gray-200 rounded-md px-4 py-2 text-sm text-gray-600 cursor-not-allowed"
                                    />
                                </div>

                                {/* Certificate Term */}
                                <div className="flex flex-col">
                                    <Label required>Certificate term (प्रमाणपत्राची मुदत)</Label>
                                    <input
                                        type="text"
                                        value={formData.certTerm}
                                        onChange={(e) => setFormData({ ...formData, certTerm: e.target.value })}
                                        className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                    />
                                </div>

                                {/* Eligibility */}
                                <div className="flex flex-col">
                                    <Label required>Eligibility to work (कामे करण्याची पात्रता)</Label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={formData.eligibility}
                                        className="w-full bg-[#f1f5f9] border border-gray-200 rounded-md px-4 py-2 text-sm text-gray-600 cursor-not-allowed"
                                    />
                                </div>

                                {/* Registration Number */}
                                <div className="flex flex-col">
                                    <Label required>Registration number (नोंदणी क्रमांक)</Label>
                                    <input
                                        type="text"
                                        placeholder="For ex. 123/22"
                                        value={formData.regNo}
                                        onChange={(e) => setFormData({ ...formData, regNo: e.target.value })}
                                        className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                    />
                                </div>

                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-2 rounded text-sm font-semibold shadow-md transition-all active:scale-95"
                                >
                                    Submit
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </InchargeLayout>
    );
};

export default GenerateCertificate;
